import * as React from "react";
import { Toast } from "react-ui";

import { arrayHasLength, arrayIsNotEmpty, noop } from "../../../../../../Common/TypingUtils";
import {
    CLEAR_PAGE,
    CLEAR_PAGE_MODAL_OPEN,
    COPY_UNIT_CLICK,
    ENABLE_AUTO_VALUES_PAGE,
    REMOVE_PAGE_MODAL_OPEN,
    REMOVE_UNIT_CLICK,
} from "../../../metricsAction";
import { PushInstance } from "../../../Internals/ControlContexts/PathContexts/PushInstance";
import { IUnitController, IUnitRegistry } from "../../Navigation/UnitRegistry/IUnitRegistry";
import {
    PageActionsContainer,
    UnitContentInner,
    UnitContentInnerScrollable,
    UnitsLayoutItem,
} from "../../Navigation/UnitsLayout/UnitsLayout";
import { mapPageActionsByProps } from "../Internals/mapPageActions";
import { PageAction, PageActions } from "../Internals/PageActions";
import { UnitLoader } from "../Internals/UnitLoader/UnitLoader";
import { UnitIdProvider } from "../UnitIdContext";
import { UnitLayoutKind } from "../UnitLayoutKind";
import { UnitId, unitIdToLegacyUnitId, unitIdWithInstance } from "../UnitId/UnitId";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { ProvideInstanceIndex } from "../../../Internals/ControlContexts/PathContexts/InstanceIndexContext";
import { TestPageObjectHandle } from "../../../Services/TestPageObjects/TestPageObjectHandle";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { usePartialRender } from "../../Navigation/UnitPartialRendering/PartiallyRenderedUnit";
import { useUnitLayoutingContext } from "../Internals/UnitBorderContext/UnitLayoutingContext";
import { ClearPageMethod } from "../../../../Engine/ActionCreators/UnitActionCreators";
import { UnitClearConfirmModal } from "../Modal/UnitClearConfirmModal";
import { ChildPage, UnitClearConfirmationSettings } from "../Unit";
import { UnitRemoveConfirmModal } from "../Modal/UnitRemoveConfirmModal";
import { getUnitInstanceLabel } from "../../UnitLabel/UnitLabelHelpers";
import { PropsPageActions } from "../Types";
import { useUnitWidth } from "../Store/unitSelectors";
import { DisableUnitActionsContext } from "../DisableUnitActionsContext";
import { LoadedAttachmentContext } from "../LoadedAttachmentContext";

export type ActionName = "clear" | "remove" | "copy" | "enableAutoValues";

export interface MultipleUnitProps {
    bindingPath: AbsoluteResolvedModelPath;
    name?: string;
    label?: string;
    instance: string;
    disableAppend?: boolean;
    parentUnitId: UnitId;
    children: React.ReactNode;
    enableHorizontalScrolling: boolean;
    height?: number;
    index: number;
    pageActions: PropsPageActions;
    hideEnableAutoValues?: boolean;
    layoutKind: UnitLayoutKind;
    allowRemove: boolean;
    showUnitBorder: boolean;
    noContent?: boolean;
    drawAsPageStack?: boolean;
    unitRegistry: IUnitRegistry;
    pages: ChildPage[];
    clearConfirmationSettings: UnitClearConfirmationSettings;
    isReadOnly?: boolean;
    maxOccurs?: number;
    onHighlightUnit: (unitId: UnitId) => void;
    onRemoveUnit: (instance: string, id: UnitId) => void;
    onCopyUnit: (instance: string, id: UnitId) => Promise<void>;
    onClearUnit?: (instance: string, method: ClearPageMethod) => void;
    onEnableAutoValues?: (instance: string) => void;
    onCheckModalUnitDataLoaded?: (bindingPath: AbsoluteModelPath, instance: string) => void;
    extraPageActions?: PageAction[];
    excludedPageActions?: ActionName[];
    modalTitle?: string;
    onMount: () => void;
    onUnmount: () => void;
}

export function MultipleUnit(props: MultipleUnitProps): JSX.Element {
    const track = useMetrics();
    const [loaded, partialLoadRef] = usePartialRender();
    const { marginTop, unitContextRef } = useUnitLayoutingContext();
    const [submitRemoveModalOpened, setSubmitRemoveModalOpened] = React.useState(false);
    const [submitClearModalOpened, setSubmitClearModalOpened] = React.useState(false);

    const {
        layoutKind,
        showUnitBorder,
        pageActions = {},
        unitRegistry,
        bindingPath,
        instance,
        enableHorizontalScrolling,
        index,
        parentUnitId,
        label,
        name,
        onCheckModalUnitDataLoaded,
        modalTitle,
    } = props;
    const hasChildPages = props.pages.length > 0;
    const modalHeader = React.useMemo(
        () => (!!name ? `${name} ${!!label ? label : getUnitInstanceLabel(index, label)}` : null),
        [name, index, label]
    );

    const unit = React.useRef<HTMLDivElement | null>(null);

    const unitRef = (element: HTMLDivElement | null) => {
        unit.current = element;
        if (unitContextRef) {
            unitContextRef.current = element;
        }
        partialLoadRef(element);
    };

    const checkInstanceIsLoaded = () => {
        if (onCheckModalUnitDataLoaded != undefined) {
            onCheckModalUnitDataLoaded(bindingPath, instance);
        }
    };

    const unitId = React.useMemo(() => unitIdWithInstance(parentUnitId, instance), [parentUnitId, instance]);
    const legacyParentUnitId = React.useMemo(() => unitIdToLegacyUnitId(parentUnitId), [parentUnitId]);
    const pageObjectId = React.useMemo(() => bindingPath.joinWith(instance).toLegacyPath(), [bindingPath, instance]);

    const unitWidth = useUnitWidth(unitId);

    const controller = React.useMemo<IUnitController>(
        () => ({
            getRootElement: (): HTMLElement | undefined => unit.current ?? undefined,
            ensureUnitAvailableForScroll: noop,
            scrollIntoView: () => {
                unit.current?.scrollIntoView();
            },
        }),
        []
    );

    React.useEffect(() => {
        props.onMount();

        return () => {
            props.onUnmount();
        };
    }, []);

    React.useEffect(() => {
        unitRegistry.registerUnit(unitId, controller);
        return () => unitRegistry.unregisterUnit(unitId);
    }, [unitId]);

    React.useEffect(() => {
        checkInstanceIsLoaded();
    }, [instance]);

    const [unitContent, ...nestedUnits] = React.Children.toArray(props.children);

    const handleRemove = React.useCallback((): void => {
        setSubmitRemoveModalOpened(true);
        track(REMOVE_PAGE_MODAL_OPEN, { page: legacyParentUnitId });
    }, [legacyParentUnitId]);

    const handleRemovePage = () => {
        track(REMOVE_UNIT_CLICK, { page: legacyParentUnitId });
        setSubmitRemoveModalOpened(false);
        props.onRemoveUnit(instance, parentUnitId);
    };

    const handleCopy = React.useCallback(async () => {
        track(COPY_UNIT_CLICK, { pageId: legacyParentUnitId });
        await props.onCopyUnit(instance, parentUnitId);
        Toast.push("Раздел скопирован");
    }, [instance, parentUnitId]);

    const handleClear = React.useCallback((): void => {
        setSubmitClearModalOpened(true);
        track(CLEAR_PAGE_MODAL_OPEN, { page: legacyParentUnitId });
    }, [legacyParentUnitId]);

    const handleClearPage = (method: ClearPageMethod) => {
        track(CLEAR_PAGE, { page: legacyParentUnitId, method: method });
        setSubmitClearModalOpened(false);
        props.onClearUnit?.(instance, method);
    };

    const handleEnableAutoValues = React.useCallback((): void => {
        track(ENABLE_AUTO_VALUES_PAGE, { pageId: legacyParentUnitId });
        if (props.onEnableAutoValues != undefined) {
            props.onEnableAutoValues(instance);
        }
    }, []);

    const handleMouseEnter = () => {
        props.onHighlightUnit(unitId);
    };

    const defaultPageActions = React.useMemo(() => {
        const extraActions: Record<ActionName, PageAction> | {} =
            props.onEnableAutoValues != undefined && !props.hideEnableAutoValues
                ? {
                      enableAutoValues: {
                          "data-tid": `EnableAutoValues`,
                          description: "Включить авторасчет",
                          handler: handleEnableAutoValues,
                          disabled: false,
                      },
                  }
                : {};

        const actions: { [name: string]: PageAction } = {
            ...extraActions,
            remove: {
                "data-tid": "Remove",
                description: "Удалить раздел",
                handler: handleRemove,
                disabled: false,
            },
            copy: {
                "data-tid": "CopyMultipleUnit",
                description: "Копировать раздел",
                handler: handleCopy,
                disabled: false,
            },
        };

        if (props.onClearUnit) {
            actions["clear"] = {
                "data-tid": "Clear",
                description: "Очистить раздел",
                handler: handleClear,
                disabled: false,
            };
        }

        const excludedPageActions = props.excludedPageActions ?? [];
        for (const actionName of excludedPageActions) {
            // @ts-ignore
            delete actions[actionName];
        }

        return actions;
    }, [handleClear, handleRemove, handleCopy, handleEnableAutoValues]);

    const disableUnitActionsContext = React.useContext(DisableUnitActionsContext);
    const disableUnitActions = disableUnitActionsContext?.disableUnitActions;

    const mappedActions = React.useMemo(() => {
        const actions = mapPageActionsByProps(props.pageActions.actions, defaultPageActions).map(action => {
            if (action["data-tid"] === "Remove" && (!props.allowRemove || disableUnitActions?.disableRemove === true)) {
                return { ...action, disabled: true };
            }
            if (
                action["data-tid"] === "CopyMultipleUnit" &&
                (props.disableAppend || disableUnitActions?.disableCopy === true)
            ) {
                return { ...action, disabled: true };
            }
            if (action["data-tid"] === "Clear" && disableUnitActions?.disableClear === true) {
                return { ...action, disabled: true };
            }
            return action;
        });

        if (props.extraPageActions?.length && disableUnitActions?.hideExtraActions !== true) {
            return actions.concat(props.extraPageActions);
        }

        return actions;
    }, [props.pageActions.actions, defaultPageActions, props.allowRemove, props.disableAppend, disableUnitActions]);

    const loadedAttachmentFormContext = React.useContext(LoadedAttachmentContext);

    const pageActionsDisplayed =
        loaded && mappedActions.length > 0 && !pageActions.hideButton && !loadedAttachmentFormContext?.isFormEditorOpen;
    const pageActionsDisabled =
        pageActions.disableButton || (arrayHasLength(mappedActions, 1) && mappedActions[0].disabled);

    const renderInner = (children: React.ReactNode): React.ReactNode => {
        const innerContent = (
            <ProvideInstanceIndex bindingPath={bindingPath} index={index}>
                <PushInstance bindingPath={bindingPath} instance={instance}>
                    {children}
                </PushInstance>
            </ProvideInstanceIndex>
        );

        return enableHorizontalScrolling ? (
            <UnitContentInnerScrollable>{innerContent}</UnitContentInnerScrollable>
        ) : (
            <UnitContentInner>{innerContent}</UnitContentInner>
        );
    };

    return (
        <UnitIdProvider id={unitId}>
            <TestPageObjectHandle id={pageObjectId} pageObject={{ remove: handleRemove }}>
                {submitRemoveModalOpened && (
                    <UnitRemoveConfirmModal
                        name={modalTitle ?? modalHeader}
                        hasChildren={hasChildPages}
                        removeChildrenText={props.clearConfirmationSettings.removeChildrenText}
                        onRemove={handleRemovePage}
                        onClose={() => setSubmitRemoveModalOpened(false)}
                    />
                )}
                {submitClearModalOpened && (
                    <UnitClearConfirmModal
                        name={modalTitle ?? modalHeader}
                        hasChildren={hasChildPages}
                        clearChildrenCustomText={props.clearConfirmationSettings.clearChildrenCustomText}
                        keepChildrenCustomText={props.clearConfirmationSettings.keepChildrenCustomText}
                        onClear={handleClearPage}
                        onClose={() => setSubmitClearModalOpened(false)}
                    />
                )}
                {!props.noContent && (
                    <UnitsLayoutItem
                        ref={unitRef}
                        drawAsPageStack={props.drawAsPageStack}
                        layoutKind={layoutKind}
                        showBorder={showUnitBorder}
                        minHeight={props.height}
                        maxWidth={unitWidth}
                        marginTop={marginTop}
                        onMouseEnter={handleMouseEnter}>
                        {pageActionsDisplayed && !props.isReadOnly && (
                            <PageActionsContainer>
                                <PageActions
                                    caption={pageActions.caption || "Действия с разделом"}
                                    disabled={pageActionsDisabled}
                                    actions={mappedActions}
                                    instancePath={pageObjectId}
                                />
                            </PageActionsContainer>
                        )}
                        {!loaded ? <UnitLoader /> : renderInner(unitContent)}
                    </UnitsLayoutItem>
                )}
                {arrayIsNotEmpty(nestedUnits) && (
                    <PushInstance bindingPath={bindingPath} instance={instance}>
                        {nestedUnits}
                    </PushInstance>
                )}
            </TestPageObjectHandle>
        </UnitIdProvider>
    );
}
